'use client';

import { type RefObject, useEffect, useState } from 'react';

import { type ThemeScopeValue, isThemeScopeValue } from './constants';

/**
 * The theme scope an element sits in, so a PORTALED surface can be stamped with
 * it.
 *
 * `ThemeScope` pins a subtree by setting `data-theme` on a wrapper, and the
 * palette blocks in `globals.css` key off that attribute. A Radix
 * `Dialog`/`Popover`/`AlertDialog` portals its content to `document.body`,
 * which is OUTSIDE that wrapper — so the portaled panel skips the pin and
 * resolves its roles against `<html>` instead, i.e. the app theme (the
 * creator's choice, else `DEFAULT_CREATOR_THEME`). That is the whole reason a
 * black country-code list and a black "No Account Found" confirm appeared on
 * the white pre-auth funnel, which pins itself light exactly as designed.
 *
 * React context cannot fix it either — the panel IS in the React tree, it is
 * the DOM ancestry that breaks — so the value is read off the DOM and handed
 * across as an attribute. `<html>` always carries `data-theme` (`ThemeScript`
 * stamps it before first paint), so this always resolves to something.
 *
 * Read on mount, and again whenever `refreshOn` changes — pass the flag that
 * opens the portaled surface. Mount alone is not enough (the creator can toggle
 * the app theme while this stays mounted) and open alone is not enough either:
 * the answer would arrive a frame AFTER the panel first paints, which is one
 * frame of the wrong ramp.
 */
export function useScopedTheme(
  ref: RefObject<HTMLElement | null>,
  refreshOn?: unknown,
): ThemeScopeValue | undefined {
  const [theme, setTheme] = useState<ThemeScopeValue>();

  useEffect(() => {
    const scope = ref.current?.closest('[data-theme]');
    const value = scope?.getAttribute('data-theme');
    setTheme(isThemeScopeValue(value) ? value : undefined);
  }, [ref, refreshOn]);

  return theme;
}
